import { useEffect, useState } from 'react';
import { supabaseService } from '@/shared/services/supabaseService';

export interface RecentQuote {
  id: string;
  quote_number: string;
  title: string;
  client_id: string;
  total_amount: number;
  status: string;
  created_at: string;
}

export interface RecentInvoice {
  id: string;
  invoice_number: string;
  title: string;
  client_id: string;
  total_amount: number;
  paid_amount: number;
  status: string;
  created_at: string;
}

export interface RecentPayment {
  id: string;
  amount: number;
  status: string;
  payment_date: string;
  created_at: string;
}

export interface UserActivity {
  recentQuotes: RecentQuote[];
  recentInvoices: RecentInvoice[];
  recentPayments: RecentPayment[];
}

/**
 * Hook to fetch recent user activity from the database
 * Returns the 5 most recent quotes, invoices, and payments
 */
export const useUserActivity = (userId: string | undefined) => {
  const [activity, setActivity] = useState<UserActivity>({
    recentQuotes: [],
    recentInvoices: [],
    recentPayments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchActivity = async () => {
      try {
        setLoading(true);
        setError(null);
        const client = supabaseService.client;

        // Fetch recent quotes (last 5)
        const { data: quotes, error: quotesError } = await client
          .from('quotes')
          .select('id, quote_number, title, client_id, total_amount, status, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (quotesError) throw quotesError;

        // Fetch recent invoices (last 5)
        const { data: invoices, error: invoicesError } = await client
          .from('invoices')
          .select('id, invoice_number, title, client_id, total_amount, paid_amount, status, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (invoicesError) throw invoicesError;

        // Fetch recent payments (last 5)
        const { data: payments, error: paymentsError } = await client
          .from('payments')
          .select('id, amount, status, payment_date, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (paymentsError) throw paymentsError;

        setActivity({
          recentQuotes: quotes || [],
          recentInvoices: invoices || [],
          recentPayments: payments || [],
        });
      } catch (err) {
        console.error('Error fetching user activity:', err);
        setError(err as Error);
        // Set empty arrays on error
        setActivity({
          recentQuotes: [],
          recentInvoices: [],
          recentPayments: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [userId]);

  return { activity, loading, error };
};
